import { ScrollableArea } from "@nova/component-scrollable-area"
import { ReactNode, useMemo } from "react"
import { matchPath, useLocation } from "react-router"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import { NovaRouteTree, useRouter } from "pages"
import { GetScopedTo } from "@nova/react-router"

export function GlobalNavNestedSidebar({
    title,
    children,
    footer,
}: {
    title: ReactNode
    children?: ReactNode
    footer?: ReactNode
}) {
    return (
        <div className="w-[240px] min-w-[240px] border-r border-r-gray-700 flex flex-col">
            <div className="h-[74px] min-h-[74px] border-b border-b-gray-700 px-6 flex justify-between items-center">
                <div className="text-[1.375rem] telegraf">{title}</div>
            </div>
            <ScrollableArea className="grow">{children}</ScrollableArea>
            {footer}
        </div>
    )
}

export function GlobalNavNestedSidebarSection({
    children,
    title,
}: {
    children?: ReactNode
    title?: string
}) {
    return (
        <div className="flex flex-col gap-px">
            {title && <small className="p-2 xs text-gray-300">{title}</small>}
            {children}
        </div>
    )
}

export type GlobalNavNestedSidebarItemProps = {
    to: GetScopedTo<NovaRouteTree, "/workspace/:workspaceId">
    Icon: Icons.IconComponent
    title: string
    nested?: GlobalNavNestedSidebarItemProps[]
    subRoutes?: string[]
    isNested?: boolean
}

export function GlobalNavNestedSidebarItem({
    to,
    Icon,
    title,
    subRoutes = [],
    nested = [],
    isNested = false,
}: GlobalNavNestedSidebarItemProps) {
    const router = useRouter("/workspace/:workspaceId")
    const linkTo = nested && nested.length > 0 ? nested[0].to : to
    const location = useLocation()
    const hasNested = nested.length > 0
    const isActive = useMemo(
        () =>
            !!matchPath({ path: router.preview(to), end: !hasNested }, location.pathname) ||
            subRoutes.some((route) => route === location.pathname) ||
            nested.some(
                (item) =>
                    !!matchPath({ path: router.preview(item.to), end: true }, location.pathname)
            ),
        [to, subRoutes, nested, hasNested, router, location.pathname]
    )
    return (
        <>
            <router.Link to={linkTo}>
                <div
                    className={classNames(
                        "w-full flex gap-2.5 p-2 items-center group transition-colors rounded-lg",
                        {
                            "hover:bg-white-100": !isActive,
                            "bg-white-200": isActive && !hasNested,
                        }
                    )}
                >
                    <Icons.SmallIcon
                        Icon={Icon}
                        iconClassName={classNames("transition-colors", {
                            "text-blue-500": isActive && !isNested,
                            "text-white": isActive && isNested,
                            "text-gray-300 group-hover:text-white": !isActive,
                        })}
                    />
                    <div>{title}</div>
                </div>
            </router.Link>
            {isActive && nested.length > 0 && (
                <div className="flex gap-3 py-2">
                    <div className="w-px self-stretch bg-white-200" />
                    <div className="grow flex flex-col gap-[3px]">
                        {nested.map((item, i) => (
                            <GlobalNavNestedSidebarItem key={i} {...item} isNested />
                        ))}
                    </div>
                </div>
            )}
        </>
    )
}
